// EPOCH 35 - Phase 3: CSR User Selector
// Allows CSRs to select a user to act on behalf of

import React, { useState } from 'react';
import { useCSRContext } from '../../hooks/useCSRContext';

interface CSRUserSelectorProps {
  onUserSelected?: () => void;
  className?: string;
}

export const CSRUserSelector: React.FC<CSRUserSelectorProps> = ({ 
  onUserSelected,
  className = ''
}) => {
  const { isCSR, targetUser, availableUsers, switchToUser } = useCSRContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  if (!isCSR) {
    return null;
  }

  const filteredUsers = availableUsers.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    user.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectUser = (user: typeof availableUsers[0]) => {
    switchToUser(user);
    setIsOpen(false);
    setSearchTerm('');
    if (onUserSelected) {
      onUserSelected();
    }
  };

  return (
    <div className={`relative ${className}`}>
      {targetUser ? (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-purple-600 font-medium">Acting as:</div>
              <div className="font-semibold text-gray-900">
                {targetUser.full_name || targetUser.email}
              </div>
              <div className="text-sm text-gray-600">{targetUser.company_name}</div>
            </div>
            <button
              onClick={() => switchToUser(null)}
              className="text-purple-600 hover:text-purple-800 p-2"
              title="Clear selection"
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        <div>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
          >
            <span>🎭</span>
            <span>Select User to Act On Behalf Of</span>
          </button>
        </div>
      )}

      {isOpen && !targetUser && (
        <div className="absolute top-full mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden">
          <div className="p-3 border-b">
            <input
              type="text"
              placeholder="Search users by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              autoFocus
            />
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchTerm ? 'No users found matching your search' : 'No users available'}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className="w-full px-4 py-3 hover:bg-purple-50 transition-colors text-left"
                  >
                    <div className="font-medium text-gray-900">
                      {user.full_name || user.email}
                    </div>
                    <div className="text-sm text-gray-600">
                      {user.email} • {user.company_name}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="p-2 border-t bg-gray-50">
            <button
              onClick={() => setIsOpen(false)}
              className="w-full px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};